import { useEffect, useRef, useState } from 'react';
import {
  responseTemplateForPath,
  type ToolResponseProjection,
} from '@reach/shared-types/tool-response-projection';
import { MonacoJsonEditor } from '../../components/MonacoJsonEditor';
import { DiscoveredResponseFields } from './DiscoveredResponseFields';
import { JsonField } from './JsonField';
import { RequiredResponseMapping } from './RequiredResponseMapping';
import { ResponseFieldRow } from './ResponseFieldRow';
import { ResponseMappingPreview } from './ResponseMappingPreview';
import {
  isResponseMappingRepresentable,
  likelyIdentifierPath,
  responseMappingRepresentabilityIssue,
  responseMappingToRows,
  responseOutputNameForPath,
  rowsToResponseMapping,
  validateAdvancedResponseMapping,
  sampleResponsePaths,
  validateResponseRows,
  type ResponseFieldRow as ResponseFieldRowModel,
} from './response-mapping';
import { ToolFormField as Field } from './ToolFormField';
import { ToolFormSection as Section } from './ToolFormSection';
import { AGENT_OUTCOME_COPY } from './agent-outcome-copy';
import { RESPONSE_MAPPING_COPY } from './response-mapping-copy';
import styles from './ResponseMapping.module.css';
import toolFormStyles from './ToolForm.module.css';

interface ResponseFieldRowWithId extends ResponseFieldRowModel {
  id: number;
}

const assignRowIds = (
  rows: ResponseFieldRowModel[],
  nextRowId: { current: number },
): ResponseFieldRowWithId[] => rows.map(row => ({ ...row, id: nextRowId.current++ }));

const SAMPLE_PATH_LIST_ID = 'response-sample-paths';
interface ResponseMappingSectionProps {
  step: number;
  mapping: ToolResponseProjection | null;
  sampleResponse?: unknown;
  requiredOutputName?: string;
  guidedOutputName?: string;
  onChange: (mapping: ToolResponseProjection | null) => void;
  onJsonValidityChange: (isValid: boolean) => void;
  onRowsValidityChange: (isValid: boolean) => void;
  onInteraction?: () => void;
}

export const ResponseMappingSection = ({
  step,
  mapping,
  sampleResponse,
  requiredOutputName,
  guidedOutputName,
  onChange,
  onJsonValidityChange,
  onRowsValidityChange,
  onInteraction,
}: ResponseMappingSectionProps) => {
  const nextRowId = useRef(0);
  const [rows, setRows] = useState<ResponseFieldRowWithId[]>(() =>
    assignRowIds(responseMappingToRows(mapping), nextRowId),
  );
  const [rawMode, setRawMode] = useState(() => !isResponseMappingRepresentable(mapping));
  const [sampleText, setSampleText] = useState(() => stringifySample(sampleResponse));
  const [rawMappingIsValid, setRawMappingIsValid] = useState(true);
  const seededSampleRef = useRef(sampleResponse === undefined ? '' : stringifySample(sampleResponse));

  // External test results may seed the scratch sample, but render-time derivation would erase edits.
  useEffect(() => {
    if (sampleResponse === undefined) return;
    const nextSample = stringifySample(sampleResponse);
    if (nextSample === seededSampleRef.current) return;
    seededSampleRef.current = nextSample;
    setSampleText(nextSample);
  }, [sampleResponse]);

  const emittedMappingRef = useRef(JSON.stringify(mapping));

  // Reconcile local editor state when the parent replaces the draft mapping.
  useEffect(() => {
    const incoming = JSON.stringify(mapping);
    if (incoming !== emittedMappingRef.current) {
      emittedMappingRef.current = incoming;
      setRows(assignRowIds(responseMappingToRows(mapping), nextRowId));
      setRawMode(!isResponseMappingRepresentable(mapping));
    }
  }, [mapping]);

  const parsedSample = parseSample(sampleText);
  const rowIssues = validateResponseRows(rows, parsedSample);
  const issueByIndex = new Map(rowIssues.map(issue => [issue.index, issue]));
  const blockingIssueCount = rowIssues.filter(issue => issue.isBlocking).length;

  // Keep the page-level save guard synchronized with simple-builder validity.
  useEffect(() => {
    onRowsValidityChange(rawMode || blockingIssueCount === 0);
  }, [rawMode, blockingIssueCount, onRowsValidityChange]);

  // Keep the page-level save guard synchronized with advanced-editor validity.
  useEffect(() => {
    if (rawMode) onJsonValidityChange(validateAdvancedResponseMapping(mapping) === null);
  }, [mapping, onJsonValidityChange, rawMode]);

  const commitRows = (nextRows: ResponseFieldRowWithId[]) => {
    setRows(nextRows);
    const nextRowsHaveBlockingIssues = validateResponseRows(nextRows).some(issue => issue.isBlocking);
    if (nextRowsHaveBlockingIssues) {
      onInteraction?.();
      return;
    }

    const nextMapping = rowsToResponseMapping(nextRows);
    emittedMappingRef.current = JSON.stringify(nextMapping);
    onChange(nextMapping);
  };

  const representabilityIssue = responseMappingRepresentabilityIssue(mapping);
  const switchToSimple = () => {
    if (!rawMode) return;
    setRows(assignRowIds(responseMappingToRows(mapping), nextRowId));
    setRawMode(false);
    setRawMappingIsValid(true);
    onJsonValidityChange(true);
  };

  const reportRawMappingValidity = (isValid: boolean) => {
    setRawMappingIsValid(isValid);
    onJsonValidityChange(isValid);
  };

  const suggestedPaths = parsedSample === undefined ? [] : sampleResponsePaths(parsedSample);
  const selectablePaths = suggestedPaths.filter(suggestion => {
    const candidate = { name: responseOutputNameForPath(suggestion.path), path: suggestion.path };
    return !validateResponseRows([candidate], parsedSample).some(issue => issue.isBlocking);
  });
  const selectedPaths = new Set(rows.map(row => row.path.trim()).filter(Boolean));
  const sampleIsUnparseable = sampleText.trim() !== '' && parsedSample === undefined;

  const availableOutputNameForPath = (path: string) => {
    const baseName = responseOutputNameForPath(path);
    const existingNames = new Set(rows.map(row => row.name));
    let name = baseName;
    let suffix = 2;
    while (existingNames.has(name)) {
      name = `${baseName}${suffix}`;
      suffix += 1;
    }
    return name;
  };
  const discoveredFields = selectablePaths.map(suggestion => ({
    ...suggestion,
    outputName: availableOutputNameForPath(suggestion.path),
  }));
  const addSuggestedPath = (path: string) =>
    commitRows([...rows, { id: nextRowId.current++, name: availableOutputNameForPath(path), path }]);
  const applyRequiredPath = (path: string, outputName: string) => {
    const template = responseTemplateForPath(path);
    if (template === null) return;
    if (rawMode) {
      const nextMapping = { ...(mapping ?? {}), [outputName]: template };
      emittedMappingRef.current = JSON.stringify(nextMapping);
      onChange(nextMapping);
      return;
    }
    const existingIndex = rows.findIndex(row => row.name === outputName);
    if (existingIndex === -1) {
      commitRows([...rows, { id: nextRowId.current++, name: outputName, path }]);
      return;
    }
    commitRows(rows.map((row, index) => (index === existingIndex ? { ...row, path } : row)));
  };

  const builderFields =
    rows.length === 0 ? (
      <p className={styles.passthroughNotice}>{RESPONSE_MAPPING_COPY.passthroughNotice}</p>
    ) : (
      <div className={styles.fieldTableScroll}>
        <table className={styles.fieldTable}>
          <thead>
            <tr>
              <th>{RESPONSE_MAPPING_COPY.agentFieldHeader}</th>
              <th>{RESPONSE_MAPPING_COPY.pathHeader}</th>
              <th aria-label={RESPONSE_MAPPING_COPY.removeHeader} />
            </tr>
          </thead>
          <tbody>
            {rows.map((row, index) => (
              <ResponseFieldRow
                key={row.id}
                row={row}
                issue={issueByIndex.get(index)}
                guidance={
                  row.name === guidedOutputName ? AGENT_OUTCOME_COPY.mappedResourceIdHint : undefined
                }
                pathListId={suggestedPaths.length > 0 ? SAMPLE_PATH_LIST_ID : undefined}
                onUpdate={patch =>
                  commitRows(
                    rows.map((existing, i) => (i === index ? { ...existing, ...patch } : existing)),
                  )
                }
                onRemove={() => commitRows(rows.filter((_, i) => i !== index))}
              />
            ))}
          </tbody>
        </table>
      </div>
    );

  return (
    <Section
      step={step}
      title={RESPONSE_MAPPING_COPY.sectionTitle}
      subtitle={RESPONSE_MAPPING_COPY.sectionSubtitle}>
      <aside className={styles.draftNotice}>
        <strong>{RESPONSE_MAPPING_COPY.draftPreviewTitle}</strong>
        <span>{RESPONSE_MAPPING_COPY.savedTestRunnerNote}</span>
      </aside>

      <Field label={RESPONSE_MAPPING_COPY.sampleFieldLabel} hint={RESPONSE_MAPPING_COPY.sampleFieldHint}>
        <MonacoJsonEditor value={sampleText} onChange={setSampleText} height="180px" />
        {sampleIsUnparseable && (
          <p className={toolFormStyles.jsonError} role="alert">
            {RESPONSE_MAPPING_COPY.invalidSampleMessage}
          </p>
        )}
      </Field>

      {!rawMode && discoveredFields.length > 0 && (
        <DiscoveredResponseFields
          suggestions={discoveredFields}
          selectedPaths={selectedPaths}
          onSelect={addSuggestedPath}
        />
      )}

      {requiredOutputName && (
        <RequiredResponseMapping
          outputName={requiredOutputName}
          suggestedPath={likelyIdentifierPath(selectablePaths)}
          onApplySuggestion={applyRequiredPath}
        />
      )}

      <div className={styles.mappingHeading}>
        <strong>{RESPONSE_MAPPING_COPY.mappingHeading}</strong>
        <span>{RESPONSE_MAPPING_COPY.mappingHeadingHint}</span>
      </div>
      <div className={toolFormStyles.editorModeRow}>
        <button
          type="button"
          aria-pressed={!rawMode}
          disabled={rawMode && representabilityIssue !== null}
          onClick={switchToSimple}>
          {RESPONSE_MAPPING_COPY.simpleMode}
        </button>
        <button
          type="button"
          aria-pressed={rawMode}
          disabled={!rawMode && blockingIssueCount > 0}
          onClick={() => setRawMode(true)}>
          {RESPONSE_MAPPING_COPY.jsonMode}
        </button>
        {rawMode && representabilityIssue && (
          <span className={toolFormStyles.editorModeReason}>{representabilityIssue}</span>
        )}
      </div>

      {rawMode ? (
        <JsonField
          label={RESPONSE_MAPPING_COPY.jsonLabel}
          hint={RESPONSE_MAPPING_COPY.jsonHint}
          value={mapping}
          validateValue={validateAdvancedResponseMapping}
          onChange={value => {
            emittedMappingRef.current = JSON.stringify(value);
            onChange(value as ToolResponseProjection | null);
          }}
          onValidityChange={reportRawMappingValidity}
          onEdit={onInteraction}
          height="200px"
        />
      ) : (
        <div className={styles.builder}>
          {builderFields}
          <button
            type="button"
            onClick={() => commitRows([...rows, { id: nextRowId.current++, name: '', path: '' }])}
            className={toolFormStyles.linkButton}>
            {RESPONSE_MAPPING_COPY.addField}
          </button>
          {suggestedPaths.length > 0 && (
            <datalist id={SAMPLE_PATH_LIST_ID}>
              {suggestedPaths.map(suggestion => (
                <option key={suggestion.path} value={suggestion.path} label={suggestion.kind} />
              ))}
            </datalist>
          )}
        </div>
      )}

      {(!rawMode || rawMappingIsValid) && (
        <ResponseMappingPreview sampleResponse={parsedSample} mapping={mapping} />
      )}
    </Section>
  );
};

function stringifySample(sampleResponse: unknown): string {
  return sampleResponse === undefined ? '' : JSON.stringify(sampleResponse, null, 2);
}

function parseSample(sampleText: string): unknown {
  if (!sampleText.trim()) return undefined;
  try {
    return JSON.parse(sampleText);
  } catch {
    return undefined;
  }
}
